/**
 * COMPONENTE DE RUTA BASADA EN ROLES
 * 
 * Este componente protege rutas basándose en los roles del usuario.
 * Permite definir roles requeridos y redirige al dashboard correspondiente
 * si el usuario no tiene los permisos necesarios.
 * 
 * @author Diego Alejandro
 * @version 3.0
 */

import { useAuth } from '../../context/authContext';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

// Configuración centralizada de roles
import { getRoleConfig, canAccessPath, getDashboardRoute } from '../../config/roleRoutes';

const RoleBasedRoute = ({ 
  children, 
  requiredRoles = [], 
  requireAll = false, 
  fallbackComponent = null,
  showAccessDenied = true
}) => {
  const { user, isAuthenticated, loading, hasRole, hasAnyRole } = useAuth();
  const location = useLocation();

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirigir a login si no está autenticado
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/authentication/sign-in" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  const userRole = user?.role;
  const currentPath = location.pathname;

  // Si no hay roles requeridos, verificar si puede acceder a la ruta actual
  if (!requiredRoles.length) {
    if (userRole && !canAccessPath(userRole, currentPath)) {
      // Redirigir al dashboard correspondiente a su rol
      const dashboardRoute = getDashboardRoute(userRole);
      return <Navigate to={dashboardRoute} replace />;
    }
    return children;
  }

  // Verificar permisos basándose en la configuración
  const hasPermission = requireAll 
    ? requiredRoles.every(role => hasRole(role))
    : hasAnyRole(requiredRoles);

  // Si no tiene permisos
  if (!hasPermission) {
    // Si hay un componente de fallback personalizado, usarlo
    if (fallbackComponent) {
      return fallbackComponent;
    }

    // Si no debe mostrar mensaje de acceso denegado, redirigir silenciosamente
    if (!showAccessDenied) {
      const dashboardRoute = getDashboardRoute(userRole);
      return <Navigate to={dashboardRoute} replace />;
    }

    // Mostrar mensaje de acceso denegado por defecto
    const userRoleInfo = getRoleConfig(userRole);
    
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        padding={3}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Acceso Denegado
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          No tienes permisos suficientes para acceder a esta página.
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Tu rol actual: {userRoleInfo.displayName}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Roles requeridos: {requiredRoles.map(role => getRoleConfig(role).displayName).join(', ')}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          component={Link} 
          to={userRoleInfo.dashboardRoute}
          sx={{ 
            mt: 2,
            backgroundColor: '#9B5DE5',
            '&:hover': {
              backgroundColor: '#7B48C4'
            }
          }}
        >
          Ir a mi Dashboard
        </Button>
      </Box>
    );
  }

  // Si tiene permisos, renderizar el contenido
  return children;
};

RoleBasedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  fallbackComponent: PropTypes.node,
  showAccessDenied: PropTypes.bool,
};

RoleBasedRoute.defaultProps = {
  requiredRoles: [],
  requireAll: false,
  fallbackComponent: null,
  showAccessDenied: true,
};

export default RoleBasedRoute;